import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, FileSignature, CheckCircle2, Download, Upload, FileText } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // strip "data:application/pdf;base64,"
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function downloadBase64Pdf(b64, filename) {
  const byteChars = atob(b64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Signatures() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [signing, setSigning] = useState(null);
  const [signingPdfPreview, setSigningPdfPreview] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ document_title: "", document_description: "", signer_name: "", signer_email: "" });
  const [pdfFile, setPdfFile] = useState(null);

  const canRequest = user && user.role !== "dealership";

  const load = async () => {
    const { data } = await api.get("/signatures");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  // When a signing modal opens with a PDF, fetch the original PDF for preview
  useEffect(() => {
    setSigningPdfPreview(null);
    if (signing?.has_pdf) {
      api.get(`/signatures/${signing.id}/pdf`).then(({ data }) => {
        const url = `data:application/pdf;base64,${data.pdf_b64}`;
        setSigningPdfPreview(url);
      }).catch(() => {});
    }
  }, [signing]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form };
      if (pdfFile) {
        if (pdfFile.size > 5 * 1024 * 1024) {
          setError("PDF must be under 5MB");
          return;
        }
        payload.pdf_data = await fileToBase64(pdfFile);
        payload.pdf_filename = pdfFile.name;
      }
      await api.post("/signatures", payload);
      setShowCreate(false);
      setForm({ document_title: "", document_description: "", signer_name: "", signer_email: "" });
      setPdfFile(null);
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const submitSignature = async (dataUrl) => {
    await api.post(`/signatures/${signing.id}/sign`, { signature_data: dataUrl });
    setSigning(null);
    load();
  };

  const downloadSigned = async (sig) => {
    const { data } = await api.get(`/signatures/${sig.id}/pdf`, { params: { signed: true } });
    downloadBase64Pdf(data.pdf_b64, `signed-${data.filename}`);
  };

  return (
    <div className="p-6 lg:p-12" data-testid="signatures-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps text-[#FF2B00]">Compliance</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">E-Signatures</h1>
          <p className="text-zinc-600 text-sm mt-2">Built-in signature pad — optional PDF attach. Stamps signature on the last page.</p>
        </div>
        {canRequest && (
          <button data-testid="sig-new-btn" onClick={() => setShowCreate(true)} className="bg-black text-white px-5 py-3 hover:bg-[#FF2B00] label-caps inline-flex items-center gap-2">
            <Plus size={16} /> New Signature Request
          </button>
        )}
      </div>

      <div className="bg-white border border-zinc-200">
        {items.length === 0 && <div className="p-12 text-center text-zinc-500" data-testid="sig-empty">No signature requests yet.</div>}
        {items.map((s) => (
          <div key={s.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-5 border-b border-zinc-100 hover:bg-zinc-50 items-center" data-testid={`sig-row-${s.id}`}>
            <div className="md:col-span-5">
              <div className="flex items-center gap-2">
                <FileSignature size={16} className="text-[#FF2B00]" />
                <div className="font-display text-lg">{s.document_title}</div>
                {s.has_pdf && <span className="label-caps text-[10px] bg-zinc-100 px-2 py-1 inline-flex items-center gap-1"><FileText size={10}/> PDF</span>}
              </div>
              {s.document_description && <div className="text-sm text-zinc-600 mt-1 ml-6">{s.document_description}</div>}
              {s.pdf_filename && <div className="text-xs text-zinc-500 ml-6 mt-1 font-mono">{s.pdf_filename}</div>}
            </div>
            <div className="md:col-span-3 text-sm">
              <div>{s.signer_name}</div>
              <div className="text-xs text-zinc-500">{s.signer_email}</div>
            </div>
            <div className="md:col-span-2 text-xs text-zinc-500">
              {s.signed_at ? new Date(s.signed_at).toLocaleDateString() : "Awaiting signature"}
            </div>
            <div className="md:col-span-2 flex justify-end gap-2 flex-wrap">
              {s.status === "signed" ? (
                <>
                  <button data-testid={`sig-view-${s.id}`} onClick={() => setViewing(s)} className="label-caps border border-black px-3 py-2 hover:bg-black hover:text-white inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> View
                  </button>
                  {s.has_signed_pdf && (
                    <button data-testid={`sig-download-${s.id}`} onClick={() => downloadSigned(s)} className="label-caps bg-black text-white px-3 py-2 hover:bg-[#FF2B00] inline-flex items-center gap-1">
                      <Download size={12} /> PDF
                    </button>
                  )}
                </>
              ) : (
                <button data-testid={`sig-sign-${s.id}`} onClick={() => setSigning(s)} className="label-caps bg-[#FF2B00] text-white px-3 py-2 hover:bg-black">
                  Sign now
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sig-create-modal">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <h2 className="font-display text-2xl">Request a Signature</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} /></button>
            </div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div><label className="label-caps block mb-1">Document Title</label>
                <input data-testid="sig-title" required value={form.document_title} onChange={(e) => setForm({ ...form, document_title: e.target.value })} className="w-full border border-black px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Description</label>
                <textarea data-testid="sig-description" rows={3} value={form.document_description} onChange={(e) => setForm({ ...form, document_description: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" /></div>
              <div>
                <label className="label-caps block mb-1">PDF Document (optional, ≤5MB)</label>
                <label className="flex items-center gap-2 border-2 border-dashed border-zinc-300 px-4 py-3 cursor-pointer hover:border-black">
                  <Upload size={16} />
                  <span className="text-sm text-zinc-600">{pdfFile ? pdfFile.name : "Click to upload PDF"}</span>
                  <input data-testid="sig-pdf-upload" type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} className="hidden" />
                </label>
                {pdfFile && (
                  <button type="button" onClick={() => setPdfFile(null)} className="text-xs label-caps text-zinc-500 hover:text-[#FF2B00] mt-2">Remove</button>
                )}
              </div>
              <div><label className="label-caps block mb-1">Signer Name</label>
                <input data-testid="sig-signer-name" required value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} className="w-full border border-black px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Signer Email</label>
                <input data-testid="sig-signer-email" type="email" required value={form.signer_email} onChange={(e) => setForm({ ...form, signer_email: e.target.value })} className="w-full border border-black px-3 py-2" /></div>
              {error && <div className="text-sm text-[#FF2B00]">{error}</div>}
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowCreate(false)} className="border border-black px-5 py-2 label-caps">Cancel</button>
                <button data-testid="sig-create-submit" type="submit" className="bg-black text-white px-5 py-2 hover:bg-[#FF2B00] label-caps">Send Request</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sign modal */}
      {signing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setSigning(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sig-sign-modal">
            <div className="p-5 border-b border-zinc-200">
              <div className="label-caps text-[#FF2B00]">Sign Document</div>
              <h2 className="font-display text-2xl mt-1">{signing.document_title}</h2>
              {signing.document_description && <p className="text-sm text-zinc-600 mt-2">{signing.document_description}</p>}
              <div className="text-xs text-zinc-500 mt-3">Signing as: <span className="font-medium text-black">{signing.signer_name}</span> ({signing.signer_email})</div>
            </div>
            {signing.has_pdf && signingPdfPreview && (
              <div className="p-5 border-b border-zinc-200">
                <div className="label-caps text-zinc-500 mb-2">Document Preview</div>
                <iframe data-testid="sig-pdf-preview" src={signingPdfPreview} title="PDF preview" className="w-full border border-zinc-300" style={{ height: 400 }} />
              </div>
            )}
            <div className="p-6">
              <SignaturePad onSave={submitSignature} onCancel={() => setSigning(null)} height={220} />
            </div>
          </div>
        </div>
      )}

      {/* View signed */}
      {viewing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="bg-white max-w-2xl w-full" onClick={(e) => e.stopPropagation()} data-testid="sig-view-modal">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="label-caps text-zinc-500">Signed</div>
                <h2 className="font-display text-2xl mt-1">{viewing.document_title}</h2>
              </div>
              <button onClick={() => setViewing(null)}><X size={20} /></button>
            </div>
            <div className="p-6">
              <div className="border-2 border-black p-4 bg-white">
                <img src={viewing.signature_data} alt="Signature" className="max-h-48 mx-auto" />
              </div>
              <div className="mt-4 text-sm text-zinc-600">
                Signed by <span className="font-medium text-black">{viewing.signer_name}</span> on {new Date(viewing.signed_at).toLocaleString()}
              </div>
              {viewing.has_signed_pdf && (
                <button onClick={() => downloadSigned(viewing)} className="mt-4 bg-black text-white px-5 py-2 hover:bg-[#FF2B00] label-caps inline-flex items-center gap-2">
                  <Download size={14} /> Download Signed PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
